import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import ImageUploader from '@/admin/ImageUploader';
import { TextField, Toggle, Section, EyebrowSizeField } from '@/admin/AdminUI';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
export default function ExperiencesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sectionMeta, setSectionMeta] = useState({});
  const [savedMeta, setSavedMeta] = useState(false);

  const load = async () => {
    const r = await api.get('/admin/experiences');
    setItems(r.data || []);
    const s = await api.get('/admin/settings');
    setSectionMeta((s.data?.sections_meta || {}).experiences || {});
  };
  useEffect(() => { load(); }, []);

  const addNew = () => setEditing({ title: '', copy: '', color: '#E8500A', image: '', url: '#', visible: true, coming_soon: false, hide_learn_more: false, order: items.length, back_description: '', back_description_present: false });
  const startEdit = (it) => {
    // v3.20 — Task 3A. Capture `back_description` presence at edit-open
    // time so we can distinguish an intentionally absent legacy record
    // from a record whose operator saved an intentional blank.
    // hasOwnProperty is the strict test: the key must exist on the
    // record itself (not inherited) to count as "present".
    const hasBack = Object.prototype.hasOwnProperty.call(it, 'back_description');
    setEditing({ ...it, back_description: hasBack ? (it.back_description ?? '') : '', back_description_present: hasBack });
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this experience?')) return;
    await api.delete(`/admin/experiences/${id}`);
    load();
  };

  const move = async (id, dir) => {
    const idx = items.findIndex((i) => i.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    await api.put('/admin/experiences/reorder', { items: next.map((it, i) => ({ id: it.id, order: i })) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: editing.title,
        copy: editing.copy,
        color: editing.color || '#E8500A',
        image: editing.image || '',
        url: editing.url || '#',
        visible: editing.visible !== false,
        coming_soon: !!editing.coming_soon,
        hide_learn_more: !!editing.hide_learn_more,
        order: typeof editing.order === 'number' ? editing.order : items.length,
      };
      // v3.20 — Task 3A. Only attach `back_description` to the payload
      // when the operator has actually engaged with the field on this
      // record. `back_description_present` is set to `true` at edit-open
      // time if the record already had the key, and it flips to `true`
      // on the first genuine onChange from the textarea below. When
      // present, the value is coerced to a string (never null): an
      // intentional blank is sent as "". When the flag is false, the
      // key is omitted entirely so the backend targeted guard keeps
      // legacy records absent.
      if (editing.back_description_present) {
        payload.back_description = typeof editing.back_description === 'string' ? editing.back_description : '';
      }
      if (editing.id) {
        await api.put(`/admin/experiences/${editing.id}`, payload);
      } else {
        await api.post('/admin/experiences', payload);
      }
      setEditing(null);
      load();
    } finally { setSaving(false); }
  };

  const saveMeta = async () => {
    const all = await api.get('/admin/settings');
    const next = { ...(all.data?.sections_meta || {}), experiences: { ...sectionMeta } };
    await api.put('/admin/settings/sections-meta', next);
    setSavedMeta(true); setTimeout(() => setSavedMeta(false), 2000);
  };
  const setMeta = (k, v) => setSectionMeta({ ...sectionMeta, [k]: v });

  // v3.15 — Eyebrow size commit handler.
  // n === null means "operator cleared the override" → REMOVE the key
  // from sectionMeta so the outgoing sections-meta payload has no
  // `eyebrow_size` and the site falls back to the 26px default.
  // Any integer 16..48 is stored as-is; the child EyebrowSizeField has
  // already rejected everything else without calling onCommit.
  const commitEyebrowSize = (n) => {
    if (n === null) {
      const next = { ...sectionMeta };
      delete next.eyebrow_size;
      setSectionMeta(next);
    } else {
      setSectionMeta({ ...sectionMeta, eyebrow_size: n });
    }
  };

  return (
    <div>
      <Section title="Section Labels">
        <TextField label="Eyebrow" value={sectionMeta.eyebrow} onChange={(v) => setMeta('eyebrow', v)} />
        <TextField label="Headline" value={sectionMeta.headline} onChange={(v) => setMeta('headline', v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Card 'Learn More' Label" value={sectionMeta.learn_more_label} onChange={(v) => setMeta('learn_more_label', v)} />
          <TextField label="Card 'Coming Soon' Badge Label" value={sectionMeta.coming_soon_badge} onChange={(v) => setMeta('coming_soon_badge', v)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="View All Button Label" value={sectionMeta.button_label} onChange={(v) => setMeta('button_label', v)} />
          <TextField label="View All Button URL" value={sectionMeta.button_url} onChange={(v) => setMeta('button_url', v)} />
        </div>
        <div className="mt-3">
          <Toggle
            label="Hide View All Experiences Button"
            value={sectionMeta.hide_view_all_button !== false}
            onChange={(v) => setMeta('hide_view_all_button', v)}
            testid="toggle-hide-view-all-experiences"
          />
        </div>
        <EyebrowSizeField
          value={sectionMeta.eyebrow_size}
          onCommit={commitEyebrowSize}
          testid="experiences-eyebrow-size"
          hint="Controls only the orange EYEBROW label above the Experiences headline."
        />
        <div className="mt-3 flex items-center gap-3">
          <button onClick={saveMeta} className="btn-primary text-[12px] !py-2 !px-4" data-testid="save-experiences-meta">Save Section Labels</button>
          {savedMeta && <span className="font-body text-[12px] text-green-700">Saved ✓</span>}
        </div>
      </Section>

      <Section title="Experiences" action={<button onClick={addNew} className="btn-primary text-[12px] !py-2.5 !px-4" data-testid="add-experience"><Plus size={14} className="mr-1" /> Add Experience</button>}>
        <div className="space-y-3">
          {items.map((it, idx) => (
            <div key={it.id} className="flex items-center gap-3 p-3 border border-border-line rounded-sm bg-white" data-testid="experience-row">
              <div className="w-16 h-16 rounded-sm overflow-hidden bg-bg-canvas shrink-0">
                {it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <div className="font-display font-bold uppercase text-ink-primary text-[14px] tracking-tightish">{it.title}</div>
                <div className="font-body text-[12px] text-ink-muted line-clamp-1">{it.copy}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: it.color }} />
                  <span className="font-body text-[11px] text-ink-soft uppercase">{it.visible !== false ? 'Visible' : 'Hidden'}</span>
                  {it.coming_soon && <span className="font-body text-[10px] uppercase tracking-wider2 bg-brand-orange text-white px-1.5 py-0.5 rounded">Coming Soon</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(it.id, -1)} disabled={idx === 0} className="p-1 disabled:opacity-30"><ChevronUp size={14} /></button>
                <button onClick={() => move(it.id, 1)} disabled={idx === items.length - 1} className="p-1 disabled:opacity-30"><ChevronDown size={14} /></button>
              </div>
              <button onClick={() => startEdit(it)} className="btn-secondary !py-2 !px-3 text-[11px]">Edit</button>
              <button onClick={() => remove(it.id)} className="btn-secondary !py-2 !px-3 text-[11px]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <div className="bg-bg-elevated max-w-2xl w-full rounded-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold uppercase text-[18px] mb-4">{editing.id ? 'Edit Experience' : 'New Experience'}</h3>
            <ImageUploader value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <TextField label="Accent Color (hex)" value={editing.color} onChange={(v) => setEditing({ ...editing, color: v })} />
            </div>
            <div className="mt-4">
              <TextField label="Description" value={editing.copy} onChange={(v) => setEditing({ ...editing, copy: v })} multiline />
            </div>
            {/* v3.20 — Task 3A. Tile back-face description override.
                Neutral placeholder only — never prefilled with the
                hardcoded legacy fallback, since prefilling risks
                flipping a genuinely absent field into a "present blank"
                on the very first save. The onChange handler flips
                `back_description_present` to true; from then on the
                key is sent (even when cleared, in which case an empty
                string is sent — never null, never omitted). */}
            <div className="mt-4">
              <TextField
                label="Back-Face Description (optional override)"
                value={editing.back_description || ''}
                onChange={(v) => setEditing({ ...editing, back_description: v, back_description_present: true })}
                multiline
                placeholder="Text shown on the back of this tile"
                testid="experience-back-description"
              />
              <p className="mt-1 font-body text-[11px] text-ink-muted">Leave untouched to keep the tile&apos;s built-in text. Type to override; clear to save an intentional blank (nothing renders on the back).</p>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Learn More URL" value={editing.url} onChange={(v) => setEditing({ ...editing, url: v })} />
              <Toggle label="Visible on site" value={editing.visible !== false} onChange={(v) => setEditing({ ...editing, visible: v })} />
            </div>
            <div className="mt-4">
              <Toggle label="Coming Soon (replaces Learn More with badge, disables link)" value={!!editing.coming_soon} onChange={(v) => setEditing({ ...editing, coming_soon: v })} />
            </div>
            <div className="mt-4">
              <Toggle label="Hide Learn More text" value={!!editing.hide_learn_more} onChange={(v) => setEditing({ ...editing, hide_learn_more: v })} testid="toggle-hide-learn-more" />
              <p className="mt-1 font-body text-[11px] text-ink-muted">(hides the LEARN MORE link entirely — overrides Coming Soon if both are checked)</p>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-[12px] !py-2 !px-4">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary text-[12px] !py-2 !px-4">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
